import type {
  TeamViewsAccountRow,
  TeamViewsPerson,
} from "../../../Store/Reducers/TeamViewsSlice";
import {
  ACCOUNT_TEAM_AVATAR_BG,
  EXEC_SPONSOR_AVATAR_BG,
} from "../../../Utils/Helper/profileAvatarUi";

/** Roles excluded from Cards / By Person (and org-wide people fill). */
export const EXCLUDED_CARD_ROLE_SLUGS = new Set([
  "vendor_admin",
  "executive_sponsor_lead",
  "executive_leader",
  "izba_admin",
]);

export const PERSON_ROLE_FILTERS = [
  { id: "all", label: "All" },
  { id: "Sponsor", label: "Sponsor" },
  { id: "Non-sponsor Exec", label: "Non-sponsor Exec" },
  { id: "C-Level", label: "C-Level" },
  { id: "AE", label: "AE" },
  { id: "CSM", label: "CSM" },
  { id: "SE", label: "SE" },
  { id: "Additional Executive", label: "Additional Executives" },
] as const;

export const ACCOUNT_COLUMN_LABELS: Record<string, string> = {
  AE: "AE",
  CSM: "CSM",
  SE: "SE",
  TAM: "TAM",
  BCS: "BCS",
  SDR: "SDR",
  VP: "VP",
  SPONSOR: "Sponsor",
  NON_SPONSOR_EXEC: "Non-sponsor Exec",
  EXTRA_SPONSOR: "Additional Executives",
};

export const DEFAULT_ACCOUNT_COLUMNS = [
  "AE",
  "CSM",
  "SE",
  "TAM",
  "BCS",
  "SDR",
  "VP",
  "SPONSOR",
  "NON_SPONSOR_EXEC",
  "EXTRA_SPONSOR",
];

/**
 * By Account core roles (Show all roles OFF). Keys match ACCOUNT_COLUMN_LABELS.
 * Display order: Sponsor → Additional Executive → AE → CSM → SE → VP.
 */
export const PREFERRED_ACCOUNT_ROLE_COLUMNS = [
  "SPONSOR",
  "EXTRA_SPONSOR",
  "AE",
  "CSM",
  "SE",
  "VP",
] as const;

export function personRoleLabel(person: TeamViewsPerson): string {
  return person.role_label || person.role_key || person.role_name || "";
}

function roleSlug(value?: string | null): string {
  return (value || "").trim().toLowerCase().replace(/\s+/g, "_");
}

export function isExcludedTeamViewsPerson(person: TeamViewsPerson): boolean {
  return (
    EXCLUDED_CARD_ROLE_SLUGS.has(roleSlug(person.role_name)) ||
    EXCLUDED_CARD_ROLE_SLUGS.has(roleSlug(person.role_key)) ||
    EXCLUDED_CARD_ROLE_SLUGS.has(roleSlug(person.role_label))
  );
}

/** Cards: every roster person except vendor admin / ESL. */
export function isCardPerson(person: TeamViewsPerson): boolean {
  return !isExcludedTeamViewsPerson(person);
}

/** Account-team population types that get the blue avatar. */
const ACCOUNT_TEAM_POPULATIONS = new Set(["account_team"]);

export function teamViewsAvatarColor(person: TeamViewsPerson): string {
  if (ACCOUNT_TEAM_POPULATIONS.has(person.population_type || "")) {
    return ACCOUNT_TEAM_AVATAR_BG;
  }
  return EXEC_SPONSOR_AVATAR_BG;
}

/** All role badges use a uniform light-purple style. */
export function teamViewsRoleBadgeClass(_roleLabel: string): string {
  return "tv-rb-default";
}

export function formatTeamViewsLastLogin(lastLoginAt?: string | null): string {
  if (!lastLoginAt) return "Not yet logged in";
  const date = new Date(lastLoginAt);
  if (Number.isNaN(date.getTime())) return "Not yet logged in";
  return `Last login ${date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })}`;
}

/** V4 activity pulse: fresh ≤14d, stale = older login, never = no login. */
export type TeamViewsActivityPulse = "fresh" | "stale" | "never";

const ACTIVITY_FRESH_DAYS = 14;

export function teamViewsActivityPulse(
  lastLoginAt?: string | null
): TeamViewsActivityPulse {
  if (!lastLoginAt) return "never";
  const date = new Date(lastLoginAt);
  if (Number.isNaN(date.getTime())) return "never";
  const ageMs = Date.now() - date.getTime();
  const ageDays = ageMs / (1000 * 60 * 60 * 24);
  return ageDays <= ACTIVITY_FRESH_DAYS ? "fresh" : "stale";
}

export function teamViewsActivityPulseLabel(
  pulse: TeamViewsActivityPulse
): string {
  if (pulse === "fresh") return "Active recently";
  if (pulse === "stale") return "Inactive";
  return "Never logged in";
}

export function formatAssignedSince(assignedSince?: string | null): string {
  if (!assignedSince) return "—";
  const date = new Date(assignedSince);
  if (Number.isNaN(date.getTime())) return "—";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

/** Role Gaps strip: "Aug 10, 2026" style vacant-since label. */
export function formatRoleGapVacantSince(vacantSince?: string | null): string {
  if (!vacantSince) return "unknown";
  const date = new Date(vacantSince);
  if (Number.isNaN(date.getTime())) return "unknown";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatRoleGapImpact(gap: {
  orphan_tier: number;
  orphaned_pair_count: number;
  orphaned_pairs?: { contact_name?: string | null }[];
}): string {
  if (gap.orphan_tier !== 3 || gap.orphaned_pair_count <= 0) {
    return "no impact";
  }
  const names = (gap.orphaned_pairs || [])
    .map(p => (p.contact_name || "").trim())
    .filter(Boolean);
  const countLabel = `${gap.orphaned_pair_count} orphaned pair${
    gap.orphaned_pair_count === 1 ? "" : "s"
  }`;
  return names.length ? `${countLabel} — ${names.join(", ")}` : countLabel;
}

export function roleGapKey(gap: {
  account_id: string;
  role_key: string;
}): string {
  return `${gap.account_id}-${gap.role_key}`;
}

/** Group people by their role label for By Person accordion view. */
export function groupPeopleByRole(
  people: TeamViewsPerson[]
): { roleLabel: string; members: TeamViewsPerson[] }[] {
  const map = new Map<string, TeamViewsPerson[]>();
  for (const p of people) {
    const label = personRoleLabel(p) || "Other";
    const members = map.get(label);
    if (members) {
      members.push(p);
    } else {
      map.set(label, [p]);
    }
  }
  // Sort groups: AE, CSM, SE first, then alphabetical
  const PRIORITY = ["AE", "CSM", "SE", "TAM", "BCS", "SDR", "VP"];
  const entries = Array.from(map.entries());
  entries.sort(([a], [b]) => {
    const ai = PRIORITY.indexOf(a);
    const bi = PRIORITY.indexOf(b);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.localeCompare(b);
  });
  return entries.map(([roleLabel, members]) => ({ roleLabel, members }));
}

export type AccountColumnPerson = {
  user_id?: string | null;
  full_name: string;
};

function cellsForAccountColumn(
  row: TeamViewsAccountRow,
  columnKey: string
): {
  user_id?: string | null;
  full_name?: string | null;
  orphan_tier?: number | null;
  vacant_since?: string | null;
}[] {
  if (columnKey === "EXTRA_SPONSOR") return row.extra_sponsors || [];
  if (columnKey === "SPONSOR") return row.sponsor || [];
  if (columnKey === "NON_SPONSOR_EXEC") return row.non_sponsor_exec || [];
  if (columnKey === "AE") return row.ae || [];
  if (columnKey === "CSM") return row.csm || [];
  if (columnKey === "SE") return row.se || [];
  return row.other_roles?.[columnKey] || [];
}

/** People assigned to a role column for one account (chip rendering). */
export function getAccountColumnPeople(
  row: TeamViewsAccountRow,
  columnKey: string,
  excludeUserId?: string | null
): AccountColumnPerson[] {
  const skipId = excludeUserId ? String(excludeUserId) : "";
  const people: AccountColumnPerson[] = [];
  for (const cell of cellsForAccountColumn(row, columnKey)) {
    const full_name = (cell.full_name || "").trim();
    if (!full_name) continue;
    if (skipId && cell.user_id != null && String(cell.user_id) === skipId) {
      continue;
    }
    people.push({ user_id: cell.user_id, full_name });
  }
  return people;
}

/** Gap metadata for an empty role cell (vacant / orphan signals). */
export type AccountColumnGap = {
  vacant_since?: string | null;
  orphan_tier?: number | null;
};

export function getAccountColumnGap(
  row: TeamViewsAccountRow,
  columnKey: string
): AccountColumnGap | null {
  if (getAccountColumnPeople(row, columnKey).length > 0) return null;
  const cells = cellsForAccountColumn(row, columnKey);
  if (!cells.length) return null;
  const gapCell = cells.find(c => c.vacant_since || c.orphan_tier != null);
  if (!gapCell) return null;
  return {
    vacant_since: gapCell.vacant_since,
    orphan_tier: gapCell.orphan_tier,
  };
}

/** Filled role columns / visible columns — account completeness (chips + Account Board). */
export function accountCoverageCompleteness(
  row: TeamViewsAccountRow,
  columns: string[]
): { filled: number; total: number } {
  const total = columns.length;
  const filled = columns.filter(col => accountColumnHasPeople(row, col)).length;
  return { filled, total };
}

export function accountColumnHasPeople(
  row: TeamViewsAccountRow,
  columnKey: string
): boolean {
  return getAccountColumnPeople(row, columnKey).length > 0;
}

/** By Account chip filters — high-signal vacant seats. */
export type AccountFilterId =
  | "all"
  | "missing_sponsor"
  | "missing_ae"
  | "missing_csm";

export const ACCOUNT_FILTER_DEFS: {
  id: AccountFilterId;
  label: string;
  /** Hide chip when count is 0 (All always shown). */
  hideWhenEmpty?: boolean;
}[] = [
  { id: "all", label: "All" },
  { id: "missing_sponsor", label: "No Sponsor", hideWhenEmpty: true },
  { id: "missing_ae", label: "No AE", hideWhenEmpty: true },
  { id: "missing_csm", label: "No CSM", hideWhenEmpty: true },
];

/** Core preferred columns available in the current column set. */
export function accountFilterCoreColumns(columns: string[]): string[] {
  const available = new Set(columns);
  return PREFERRED_ACCOUNT_ROLE_COLUMNS.filter(key => available.has(key));
}

export function accountMatchesFilter(
  row: TeamViewsAccountRow,
  filterId: AccountFilterId,
  coreColumns: string[]
): boolean {
  if (filterId === "all") return true;
  if (filterId === "missing_sponsor") {
    return (
      coreColumns.includes("SPONSOR") && !accountColumnHasPeople(row, "SPONSOR")
    );
  }
  if (filterId === "missing_ae") {
    return coreColumns.includes("AE") && !accountColumnHasPeople(row, "AE");
  }
  if (filterId === "missing_csm") {
    return coreColumns.includes("CSM") && !accountColumnHasPeople(row, "CSM");
  }
  return true;
}

/**
 * Role columns for By Account.
 * - showAllRoles OFF: only core roles (Sponsor, Additional Executive, AE, CSM, SE, VP).
 * - showAllRoles ON: every role present on the filtered accounts, core order first then the rest.
 */
export function visibleAccountColumns(
  columns: string[],
  rows: TeamViewsAccountRow[],
  showAllRoles: boolean
): string[] {
  const available = new Set(columns);
  const preferred = PREFERRED_ACCOUNT_ROLE_COLUMNS.filter(key =>
    available.has(key)
  );

  if (!showAllRoles) return [...preferred];

  const present = columns.filter(columnKey =>
    rows.some(row => accountColumnHasPeople(row, columnKey))
  );
  const presentSet = new Set(present);
  const orderedPreferred = preferred.filter(key => presentSet.has(key));
  const rest = present.filter(
    key => !(PREFERRED_ACCOUNT_ROLE_COLUMNS as readonly string[]).includes(key)
  );
  return [...orderedPreferred, ...rest];
}

export function formatAccountColumnCell(
  row: TeamViewsAccountRow,
  columnKey: string
): string {
  const people = getAccountColumnPeople(row, columnKey);
  return people.length ? people.map(p => p.full_name).join(", ") : "—";
}
